import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  KPICard,
  LoadingGrid,
  formatCurrency,
} from './statisticsShared';
import type { StatisticsOutletContext } from './statisticsShared';
import { useHideAmounts } from '@/hooks/useHideAmounts';

export default function ClientsTab() {
  const { data, loading, hasFinancialData } = useOutletContext<StatisticsOutletContext>();
  const { hideAmounts, amountClassName } = useHideAmounts();

  return (
    <div className="space-y-6">
      {/* Client Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Active Clients"
          value={String(data?.kpis?.active_clients ?? 0)}
          subtitle="with work this period"
          loading={loading}
        />
        <KPICard
          title="Active Matters"
          value={String(data?.kpis?.active_matters ?? 0)}
          subtitle="across all clients"
          loading={loading}
        />
        {hasFinancialData && data?.top_clients?.[0] && (
          <>
            <KPICard
              title="Largest Client"
              value={formatCurrency(data.top_clients[0].revenue)}
              subtitle={data.top_clients[0].name}
              loading={loading}
            />
            <KPICard
              title="Top 5 Concentration"
              value={`${data.top_clients.slice(0, 5).reduce((sum, c) => sum + (c.revenue_share ?? 0), 0).toFixed(0)}%`}
              subtitle="of total revenue"
              loading={loading}
            />
          </>
        )}
      </div>

      {/* Full Client Table */}
      {!loading && data?.top_clients && data.top_clients.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Client Performance</CardTitle>
            <CardDescription>All clients ranked by {hasFinancialData ? 'revenue' : 'hours'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 sticky top-0 bg-background">#</TableHead>
                    <TableHead className="sticky top-0 bg-background">Client</TableHead>
                    <TableHead className="text-right sticky top-0 bg-background">Hours</TableHead>
                    <TableHead className="text-right sticky top-0 bg-background">Entries</TableHead>
                    <TableHead className="text-right sticky top-0 bg-background">Matters</TableHead>
                    {hasFinancialData && (
                      <>
                        <TableHead className="text-right sticky top-0 bg-background">Revenue</TableHead>
                        <TableHead className="text-right sticky top-0 bg-background">Share</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell className="text-muted-foreground font-medium">{client.rank}</TableCell>
                      <TableCell className="font-medium max-w-[250px] truncate">{client.name}</TableCell>
                      <TableCell className="text-right">{client.hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-right">{client.entries}</TableCell>
                      <TableCell className="text-right">{client.matters}</TableCell>
                      {hasFinancialData && (
                        <>
                          <TableCell className={`text-right font-semibold ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(client.revenue)}</TableCell>
                          <TableCell className="text-right">
                            <span className={cn(
                              'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
                              (client.revenue_share ?? 0) > 20
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                                : (client.revenue_share ?? 0) > 10
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                                : 'bg-muted text-muted-foreground'
                            )}>
                              {(client.revenue_share ?? 0).toFixed(1)}%
                            </span>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <LoadingGrid count={4} />}
    </div>
  );
}
